/**
 * JSON Schema and prompt for the Work Item Details response
 * Used with the Ask API to retrieve detailed work item information
 */

/**
 * Prompt that triggers the WorkItemDetails intent
 */
export const WORK_ITEM_DETAILS_PROMPT = 'Get details for this work item.'

/**
 * JSON Schema for the LLM to produce structured work item details
 *
 * Note: tsgResearch is NOT in this schema — the API appends it after the LLM call
 */
export const WORK_ITEM_DETAILS_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    // Header fields
    id: {
      type: 'string',
      description: 'Unique Id to identify this item in the system',
    },
    title: {
      type: 'string',
      description: 'Work item title',
    },
    description: {
      type: 'string',
      description: 'Detailed description of the work item',
    },
    severity: {
      type: 'string',
      enum: ['critical', 'high', 'medium', 'low'],
      description: 'Severity level derived from SLAState',
    },
    category: {
      type: 'string',
      description: 'Short category label inferred from title content',
    },
    dueDate: {
      type: ['string', 'null'],
      description: 'ISO 8601 date string for due date, null if not set',
    },
    createdDate: {
      type: 'string',
      description: 'ISO 8601 date string for creation date',
    },
    updatedDate: {
      type: 'string',
      description: 'ISO 8601 date string for last update',
    },
    owner: {
      type: ['object', 'null'],
      properties: {
        id: { type: 'string' },
        displayName: { type: 'string' },
        email: { type: ['string', 'null'] },
      },
      required: ['id', 'displayName', 'email'],
      additionalProperties: false,
      description: 'Assigned owner, null if unassigned',
    },

    // Overview tab
    recentUpdates: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          type: {
            type: 'string',
            enum: ['text', 'bullet', 'link', 'mention', 'actionButton', 'checklist', 'progressStep', 'referenceLink'],
          },
          content: { type: 'string' },
          href: { type: ['string', 'null'] },
          mentionId: { type: ['string', 'null'] },
          mentionName: { type: ['string', 'null'] },
          buttonVariant: { type: ['string', 'null'], enum: ['primary', 'secondary', null] },
          tooltipContent: { type: ['string', 'null'] },
          checked: { type: ['boolean', 'null'] },
          stepStatus: { type: ['string', 'null'], enum: ['pending', 'in_progress', 'completed', 'skipped', null] },
          stepNumber: { type: ['number', 'null'] },
        },
        required: ['id', 'type', 'content', 'href', 'mentionId', 'mentionName', 'buttonVariant', 'tooltipContent', 'checked', 'stepStatus', 'stepNumber'],
        additionalProperties: false,
      },
      description: 'Recent updates for the overview tab',
    },
    nextSteps: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          type: {
            type: 'string',
            enum: ['text', 'bullet', 'link', 'mention', 'actionButton', 'checklist', 'progressStep', 'referenceLink'],
          },
          content: { type: 'string' },
          href: { type: ['string', 'null'] },
          mentionId: { type: ['string', 'null'] },
          mentionName: { type: ['string', 'null'] },
          buttonVariant: { type: ['string', 'null'], enum: ['primary', 'secondary', null] },
          tooltipContent: { type: ['string', 'null'] },
          checked: { type: ['boolean', 'null'] },
          stepStatus: { type: ['string', 'null'], enum: ['pending', 'in_progress', 'completed', 'skipped', null] },
          stepNumber: { type: ['number', 'null'] },
        },
        required: ['id', 'type', 'content', 'href', 'mentionId', 'mentionName', 'buttonVariant', 'tooltipContent', 'checked', 'stepStatus', 'stepNumber'],
        additionalProperties: false,
      },
      description: 'Next steps for the overview tab',
    },
    blockers: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          description: { type: 'string' },
          duration: { type: 'string' },
        },
        required: ['description', 'duration'],
        additionalProperties: false,
      },
      description: 'Current blockers',
    },
    unblockers: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          displayName: { type: 'string' },
          role: { type: ['string', 'null'] },
        },
        required: ['id', 'displayName', 'role'],
        additionalProperties: false,
      },
      description: 'Contacts who can help unblock',
    },
    fixGuidanceLinks: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          label: { type: 'string' },
          url: { type: 'string' },
          type: { type: 'string', enum: ['tsg_fix', 'tsg_general', 'user_video', 'ai_video'] },
        },
        required: ['id', 'label', 'url', 'type'],
        additionalProperties: false,
      },
      description: 'Fix guidance links for overview tab',
    },

    // Fix Guide tab
    tsgDocuments: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
          url: { type: 'string' },
          content: { type: ['string', 'null'] },
          type: { type: 'string', enum: ['fix_steps', 'general'] },
        },
        required: ['id', 'title', 'url', 'content', 'type'],
        additionalProperties: false,
      },
      description: 'TSG documents for the fix guide tab',
    },

    // Videos tab
    videos: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
          url: { type: 'string' },
          thumbnailUrl: { type: ['string', 'null'] },
          duration: { type: 'string' },
          type: { type: 'string', enum: ['ai_generated', 'user_created'] },
          summary: { type: ['string', 'null'] },
        },
        required: ['id', 'title', 'url', 'thumbnailUrl', 'duration', 'type', 'summary'],
        additionalProperties: false,
      },
      description: 'Video content for the videos tab',
    },

    // Automation tab
    automationScript: {
      type: ['object', 'null'],
      properties: {
        id: { type: 'string' },
        title: { type: 'string' },
        content: { type: 'string' },
        canLaunch: { type: 'boolean' },
        launchDisabledReason: { type: ['string', 'null'] },
      },
      required: ['id', 'title', 'content', 'canLaunch', 'launchDisabledReason'],
      additionalProperties: false,
      description: 'Automation script for the automation tab',
    },

    // History tab
    history: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          timestamp: { type: 'string' },
          type: { type: 'string', enum: ['status_change', 'assignment', 'comment', 'update', 'escalation'] },
          actorId: { type: 'string', description: 'Actor identifier' },
          actorDisplayName: { type: 'string', description: 'Actor display name' },
          description: { type: 'string' },
          details: { type: ['string', 'null'] },
        },
        required: ['id', 'timestamp', 'type', 'actorId', 'actorDisplayName', 'description', 'details'],
        additionalProperties: false,
      },
      description: 'History entries for the history tab',
    },

    // Status chips
    effortEstimate: {
      type: ['string', 'null'],
      enum: ['quick', 'moderate', 'extended', null],
      description: 'Estimated effort level',
    },
    aiConfidence: {
      type: ['string', 'null'],
      enum: ['high', 'medium', 'low', null],
      description: 'AI confidence level in the analysis',
    },
    confidenceSignals: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          label: { type: 'string' },
          isPositive: { type: 'boolean' },
        },
        required: ['label', 'isPositive'],
        additionalProperties: false,
      },
      description: 'Signals explaining the AI confidence level',
    },
    blockedDuration: {
      type: ['string', 'null'],
      description: 'How long the item has been blocked, e.g. "3d"',
    },

    // Metadata
    sourceSystem: {
      type: ['string', 'null'],
      description: 'Source system identifier',
    },
    externalId: {
      type: ['string', 'null'],
      description: 'External system work item ID',
    },
    relatedIssueIds: {
      type: ['array', 'null'],
      items: { type: 'string' },
      description: 'IDs of related work items',
    },
  },
  required: [
    'id', 'title', 'description', 'severity', 'category', 'dueDate', 'createdDate', 'updatedDate',
    'owner', 'recentUpdates', 'nextSteps', 'blockers', 'unblockers', 'fixGuidanceLinks',
    'tsgDocuments', 'videos', 'automationScript', 'history',
    'effortEstimate', 'aiConfidence', 'confidenceSignals', 'blockedDuration',
    'sourceSystem', 'externalId', 'relatedIssueIds',
  ],
  additionalProperties: false,
}
